/**
 * Canonical protected route patterns owned by the navigation catalog.
 *
 * React Router and the shell catalog consume these same values so a route
 * rename cannot leave a truthful-looking menu pointing at a different page.
 * Debug routes, public routes, and legacy redirects are deliberately outside
 * this owner-navigation contract.
 */
export const appRoutePatterns = {
  map: '/app',
  forestOverview: '/app/forest-overview/:propertyId',
  standDetail: '/app/stand-detail/:propertyId/:standId',
  myForests: '/app/my-forests',
  browseProperties: '/app/browse-properties',
  priceEstimate: '/app/price-estimate/:propertyId',
  operationsPlanning: '/app/operations-planning/:propertyId',
  operationsPlanningNewPlan: '/app/operations-planning/:propertyId/new-plan',
  measurements: '/app/measurements',
  measurementProperty: '/app/measurements/properties/:propertyId',
  measurementCampaign: '/app/measurements/campaigns/:campaignId',
  measurementPlot: '/app/measurements/campaigns/:campaignId/plots/:plotId',
  timberPrices: '/app/timber-prices',
  implement: '/app/implement/:propertyId',
  offerDecode: '/app/offer-decode/:propertyId',
  offerDecodeResult: '/app/offer-decode/:propertyId/results/:decodeId',
  watch: '/app/watch',
  satelliteHistory: '/app/satellite-history',
  climateHistory: '/app/climate-history',
  buyerDiligence: '/app/buyer-diligence',
  settings: '/app/settings',
  about: '/app/about',
} as const;

export type AppRoutePattern = (typeof appRoutePatterns)[keyof typeof appRoutePatterns];

export const navigationRoutePatternsByCatalogId = {
  'my-forests': appRoutePatterns.myForests,
  'browse-properties': appRoutePatterns.browseProperties,
  map: appRoutePatterns.map,
  'forest-overview': appRoutePatterns.forestOverview,
  'stand-detail': appRoutePatterns.standDetail,
  'measurement-property': appRoutePatterns.measurementProperty,
  measurements: appRoutePatterns.measurements,
  'measurement-campaign': appRoutePatterns.measurementCampaign,
  'measurement-plot': appRoutePatterns.measurementPlot,
  'price-estimate': appRoutePatterns.priceEstimate,
  'operations-planning': appRoutePatterns.operationsPlanning,
  implement: appRoutePatterns.implement,
  'offer-decode': appRoutePatterns.offerDecode,
  'offer-decode-result': appRoutePatterns.offerDecodeResult,
  watch: appRoutePatterns.watch,
  'satellite-history': appRoutePatterns.satelliteHistory,
  'climate-history': appRoutePatterns.climateHistory,
  'buyer-diligence': appRoutePatterns.buyerDiligence,
  'timber-prices': appRoutePatterns.timberPrices,
  settings: appRoutePatterns.settings,
  about: appRoutePatterns.about,
} as const;

export function protectedRouteChildPath(
  absolutePattern: string,
): string {
  const prefix = `${appRoutePatterns.map}/`;
  if (!absolutePattern.startsWith(prefix)) {
    throw new Error(
      `Protected child route "${absolutePattern}" must start with "${prefix}".`,
    );
  }
  return absolutePattern.slice(prefix.length);
}

type PatternParameterNames<P extends string> =
  P extends `${string}:${infer Name}/${infer Rest}`
    ? Name | PatternParameterNames<`/${Rest}`>
    : P extends `${string}:${infer Name}`
      ? Name
      : never;

type PatternParameters<P extends string> = [PatternParameterNames<P>] extends [never]
  ? Record<string, never>
  : Record<PatternParameterNames<P>, string | number>;

/**
 * Build a concrete in-app path from one canonical pattern. Feature surfaces
 * call this instead of re-typing `/app/...` so a route rename moves every
 * link with it; each segment value is URI-encoded exactly once.
 */
export function appRoutePath<P extends AppRoutePattern>(
  pattern: P,
  ...rest: [PatternParameterNames<P>] extends [never]
    ? [params?: PatternParameters<P>, search?: URLSearchParams | string]
    : [params: PatternParameters<P>, search?: URLSearchParams | string]
): string {
  const [params, search] = rest;
  const values = (params ?? {}) as Record<string, string | number>;
  const path = pattern.replace(/:([a-zA-Z_]\w*)/g, (_match, name: string) => {
    const value = values[name];
    if (value === undefined || value === null || String(value) === '') {
      throw new Error(`Route pattern "${pattern}" requires a value for ":${name}".`);
    }
    return encodeURIComponent(String(value));
  });
  const query = typeof search === 'string' ? search.replace(/^\?/, '') : (search?.toString() ?? '');
  return query ? `${path}?${query}` : path;
}

/**
 * The concrete path prefix of a pattern up to (and ending before) its first
 * parameter not supplied, e.g. every stand route of one forest. Return-target
 * validators use it to recognise a family of routes without re-typing paths.
 */
export function appRoutePathPrefix(
  pattern: AppRoutePattern,
  params: Readonly<Record<string, string | number>>,
): string {
  const segments: string[] = [];
  for (const segment of pattern.split('/')) {
    if (!segment.startsWith(':')) {
      segments.push(segment);
      continue;
    }
    const value = params[segment.slice(1)];
    if (value === undefined || String(value) === '') {
      return `${segments.join('/')}/`;
    }
    segments.push(encodeURIComponent(String(value)));
  }
  throw new Error(`Route pattern "${pattern}" has no unresolved parameter to prefix.`);
}
